import { Box, Flex } from "@chakra-ui/react";
import { PanInfo, Reorder } from "framer-motion";
import React, { useRef, useState } from "react";
import { MdOutlineEdit } from "react-icons/md";
import { FormattedMessage, useIntl } from "react-intl";

import { ContextMenu } from "@nens/chakra/components";
import {
  FolderIcon,
  FolderOpenIcon,
  MenuAction,
  MoveDirectoryIcon,
  RanaButton,
  RanaContextMenu,
  RanaEditable,
  RanaEditableHandle,
  RanaModal,
  TrashIcon,
} from "@nens/ranalib";

import { getIsEditable, getMaps, getOpenMap } from "../../../store/selectors";
import { usePublicationStore } from "../../../store/store";
import { Folder } from "../../../types/publication";
import { useMoveActions } from "../hooks/use-move-actions";

export function FolderItem({
  layer,
  isFirst,
  isLast,
  onDragStart,
  onDrag,
  onDragEnd,
}: {
  layer: Folder;
  isFirst: boolean;
  isLast: boolean;
  onDragStart: () => void;
  onDrag?: (event: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => void;
  onDragEnd: (event: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => void;
}) {
  const isEditable = usePublicationStore(getIsEditable);
  const toggleFolderOpen = usePublicationStore((state) => state.toggleFolderOpen);
  const removeFolder = usePublicationStore((state) => state.removeFolder);
  const moveLayersToPosition = usePublicationStore((state) => state.moveLayersToPosition);
  const moveLayersToMap = usePublicationStore((state) => state.moveLayersToMap);
  const changeLayerName = usePublicationStore((state) => state.changeLayerName);

  const editRef = useRef<RanaEditableHandle>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const intl = useIntl();
  const moveActions = useMoveActions({
    onMove: (dir) => moveLayersToPosition([layer.id], dir),
    isFirst,
    isLast,
  });
  const actions: MenuAction[] = [
    {
      label: intl.formatMessage({
        id: "publication.layer.contextmenu.rename_folder",
        defaultMessage: "Rename folder",
      }),
      icon: <MdOutlineEdit />,
      action: () => editRef.current?.startEditing(),
    },
    {
      label: intl.formatMessage({
        id: "publication.layer.contextmenu.remove_folder",
        defaultMessage: "Remove folder",
      }),
      icon: <TrashIcon />,
      action: () => setConfirmDelete(true),
    },
    moveActions,
  ];

  const maps = usePublicationStore(getMaps);
  const openMap = usePublicationStore(getOpenMap);

  if (maps.length > 1) {
    actions.push({
      label: intl.formatMessage({
        id: "publication.layer.contextmenu.move_to_map",
        defaultMessage: "Move to another map",
      }),
      icon: <MoveDirectoryIcon />,
      subActions: maps
        .filter((map) => map.id !== openMap.id)
        .map((map) => ({
          label: map.name,
          icon: <MoveDirectoryIcon />,
          action: () => moveLayersToMap([layer.id], map.id),
        })),
    });
  }

  const openFolder = () => {
    if (!layer.opened) {
      toggleFolderOpen(layer);
    }
  };

  return (
    <>
      {confirmDelete && (
        <DeleteFolderModal
          folder={layer}
          onConfirm={() => {
            removeFolder(layer);
            setConfirmDelete(false);
          }}
          onClose={() => setConfirmDelete(false)}
        />
      )}
      <Reorder.Item
        as="div"
        value={layer.id}
        data-row-id={layer.id}
        style={{ position: "relative" }}
        onDragStart={onDragStart}
        onDrag={onDrag}
        onDragEnd={onDragEnd}
      >
        <ContextMenu<HTMLDivElement>
          renderMenu={actions?.length ? () => <RanaContextMenu.Actions actions={actions} /> : null}
          onOpen={openFolder}
        >
          {(ref) => (
            <Flex
              ref={ref}
              role="group"
              _hover={{ bg: "button.tertiary.hover" }}
              alignItems="center"
              ml={0}
              gap={2}
              p={2}
              pl={4}
              cursor="pointer"
              onClick={() => toggleFolderOpen(layer)}
            >
              {layer.opened ? (
                <FolderOpenIcon color="brand.clear_creek" />
              ) : (
                <FolderIcon color="brand.clear_creek" />
              )}
              <Box flexGrow={1} overflow="hidden">
                <RanaEditable
                  key={layer.name}
                  defaultValue={layer.name}
                  onSubmit={(name) => changeLayerName(layer, name)}
                >
                  <RanaEditable.Preview
                    overflow="hidden"
                    textOverflow="ellipsis"
                    whiteSpace="nowrap"
                  />
                  <RanaEditable.Input />
                  <RanaEditable.EditHandle ref={editRef} />
                </RanaEditable>
              </Box>
              {isEditable && (
                <Box
                  px={1}
                  onClick={openFolder}
                  visibility="hidden"
                  _groupHover={{ visibility: "visible" }}
                >
                  <RanaContextMenu actions={actions} />
                </Box>
              )}
            </Flex>
          )}
        </ContextMenu>
      </Reorder.Item>
    </>
  );
}

function DeleteFolderModal({
  folder,
  onConfirm,
  onClose,
}: {
  folder: Folder;
  onConfirm: () => void;
  onClose: () => void;
}) {
  const intl = useIntl();

  return (
    <RanaModal
      isOpen
      onClose={onClose}
      topBar={intl.formatMessage({
        id: "publication.folder.deletefoldermodal.title",
        defaultMessage: "Remove folder",
      })}
      buttons={[
        <RanaButton key="cancel" variant="secondary" onClick={onClose}>
          <FormattedMessage id="publication.folder.button.cancel" defaultMessage="Cancel" />
        </RanaButton>,
        <RanaButton key="delete" variant="danger" onClick={onConfirm}>
          <FormattedMessage
            id="publication.folder.button.remove_folder"
            defaultMessage="Remove folder"
          />
        </RanaButton>,
      ]}
    >
      <FormattedMessage
        id="publication.folder.deletefoldermodal.message"
        defaultMessage='Are you sure you want to remove the folder "{name}"? {lineBreak} The layers inside will be moved to the top level.'
        values={{ name: folder.name, lineBreak: <br /> }}
      />
    </RanaModal>
  );
}
